import type { CompanyBillingPage as CompanyBillingPageTypes } from "../CompanyBillingPage/types";
import i18n from "../i18n";

export function formatCompanyBillingPlanName(planKey?: string | null, fallback = i18n.t("Unknown plan")): string {
  if (!planKey) return fallback;

  return formatPlanKey(planKey) || fallback;
}

export function formatCompanyBillingIntervalLabel(
  interval?: CompanyBillingPageTypes.Interval | string | null,
): string | null {
  if (!interval) return null;

  return interval === "monthly" ? i18n.t("Monthly") : interval === "yearly" ? i18n.t("Yearly") : null;
}

export function formatCompanyBillingPlanLabel(
  planKey?: string | null,
  interval?: CompanyBillingPageTypes.Interval | string | null,
  fallback = i18n.t("Unknown plan"),
): string {
  const name = formatCompanyBillingPlanName(planKey, fallback);
  const intervalLabel = formatCompanyBillingIntervalLabel(interval);

  return intervalLabel ? i18n.t("{{plan}} {{interval}}", { plan: name, interval: intervalLabel }) : name;
}

export function formatCompanyBillingDate(value?: string | null): string | null {
  if (!value) return null;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(date);
}

export function formatCompanyBillingRelativeDateLine(
  formatMessage: (date: string) => string,
  value?: string | null,
): string | null {
  const formattedDate = formatCompanyBillingDate(value);
  if (!formattedDate) return null;

  return formatMessage(formattedDate);
}

export function formatCompanyBillingPriceFromMinorUnits(amount?: number | null, currency?: string | null): string {
  if (amount == null || !currency) {
    return i18n.t("Unavailable");
  }

  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: currency.toUpperCase(),
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount / 100);
}

function formatPlanKey(planKey: string): string | null {
  const parts = planKey
    .trim()
    .split(/[_-]+/)
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length === 0) return null;

  return parts.map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(" ");
}
